import { QueryClient } from '@tanstack/react-query';
import { showDefaultErrorNotification } from './errorHandling';
import { AxiosError } from 'axios';
import { trackException } from './app-insights/appInsights';

const defaultQueryConfig = {
  staleTime: 2 * 60 * 1000,
  retry: (failureCount: number, error: unknown) => {
    // Don't retry on 4xx errors (client errors like 404, 400, 401, 403)
    // Only retry on 5xx (server errors) or network failures
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as AxiosError<{ detail?: string }>;
      const status = axiosError.response?.status;
      if (status && status >= 400 && status < 500) {
        return false; // Don't retry client errors
      }
    }
    // Retry up to 3 times for server errors and network failures
    return failureCount < 3;
  },
  retryDelay: (attemptIndex: number) => {
    // Exponential backoff: 3s, 5s, 15s
    const delays = [3000, 5000, 15000];
    return delays[attemptIndex] || 15000;
  },
};

const defaultMutationConfig = {
  retry: false, // Never retry mutations - they should fail immediately
  onError: (error: unknown) => {
    showDefaultErrorNotification(error);
    trackException(error);
  },
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: defaultQueryConfig,
    mutations: defaultMutationConfig,
  },
});
